import LocalManager from "./LocalManager";
import RemoteManager from "./RemoteManager";
import { Game, StorageManager, Guess } from "./Common";
import { useEffect, useState } from "react";
import StorageHelper from "./storageHelper";

class GameManager {
    private _storageManager: StorageManager;
    private static instance: GameManager | null = null;
    private _isLoggedIn: boolean = false;
    private _token: string | null = null;

    private constructor() {
        this._storageManager = new LocalManager(); // Default initialization
    }

    public static getInstance(): GameManager {
        if (!GameManager.instance) {
            GameManager.instance = new GameManager();
        }
        return GameManager.instance;
    }

    public async initialize(loggedIn: boolean, token?: string): Promise<Game> {
        try {
            this._isLoggedIn = loggedIn;
            this._token = token || null;

            // Only use RemoteManager if we have both login status AND a valid token
            // Also validate that the token looks like a JWT (has at least 2 dots)
            if (loggedIn && token && token.trim().length > 0 && token.split('.').length === 3) {
                // Before switching to RemoteManager, sync any local game data to the server
                await this.syncLocalToServer(token);
                this._storageManager = new RemoteManager();
            } else {
                if (loggedIn && token) {
                    console.warn('Invalid token format, falling back to LocalManager');
                }
                this._storageManager = new LocalManager();
            }

            const game = await this._storageManager.loadGame(token);
            return game;
        } catch (error) {
            console.error('Error during initialization:', error);
            // Ensure we always have a storage manager
            if (!this._storageManager) {
                this._storageManager = new LocalManager();
            }
            return await this._storageManager.loadGame(token);
        }
    }

    public async recordQuestion(id: number): Promise<number> {
        return await this._storageManager.recordQuestion(id, this._token || undefined);
    }

    public async recordTrivia(id: number): Promise<number> {
        return await this._storageManager.recordTrivia(id, this._token || undefined);
    }

    public async recordGuess(guess: Guess): Promise<number> {
        const result = await this._storageManager.recordGuess(guess, this._token || undefined);
        return result;
    }

    public async getCurrentGame(): Promise<Game> {
        return await this._storageManager.loadGame(this._token || undefined);
    }

    public async forceReload(): Promise<Game> {
        if (this._storageManager.forceReload) {
            return await this._storageManager.forceReload(this._token || undefined);
        } else {
            // Fallback to loadGame if forceReload not implemented
            return await this._storageManager.loadGame(this._token || undefined);
        }
    }

    public async updateGameState(loggedIn: boolean, token?: string): Promise<Game> {
        this._isLoggedIn = loggedIn;
        this._token = token || null;

        // Switch storage manager based on authentication state
        if (loggedIn && token && token.trim().length > 0 && token.split('.').length === 3) {
            // Before switching to RemoteManager, sync any local game data to the server
            await this.syncLocalToServer(token);
            this._storageManager = new RemoteManager();
        } else {
            if (loggedIn && token) {
                console.warn('Invalid token format, falling back to LocalManager');
            }
            this._storageManager = new LocalManager();
        }

        return await this._storageManager.loadGame(token);
    }

    public updateGameCompletion(completed: boolean): void {
        this._storageManager.updateGameCompletion(completed);
    }

    public isLoggedIn(): boolean {
        return this._isLoggedIn;
    }

    private async syncLocalToServer(token: string): Promise<void> {
        try {
            // Get current game date (same format as LocalManager)
            const gameName = (new Date()).toLocaleDateString('en-US', { timeZone: 'America/Denver' });
            const savedGame = StorageHelper.getItem(gameName);

            if (!savedGame) {
                console.log('No local game to sync');
                return;
            }

            const localGame: Game = JSON.parse(savedGame);

            // Check if there's any data worth syncing
            if (!localGame.asks?.length && !localGame.triviaAsks?.length && !localGame.guesses?.length) {
                console.log('Local game has no data to sync');
                return;
            }

            console.log('Syncing local game to server...', {
                asks: localGame.asks?.length || 0,
                triviaAsks: localGame.triviaAsks?.length || 0,
                guesses: localGame.guesses?.length || 0
            });

            const { buildApiEndpoint } = await import('../configuration/api');

            // Send all questions
            if (localGame.asks?.length) {
                for (const questionId of localGame.asks) {
                    try {
                        await fetch(buildApiEndpoint('/game-events', true), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                eventType: 'question',
                                eventData: { questionId }
                            })
                        });
                    } catch (error) {
                        console.error('Error syncing question:', questionId, error);
                    }
                }
            }

            // Send all trivia
            if (localGame.triviaAsks?.length) {
                for (const triviaId of localGame.triviaAsks) {
                    try {
                        await fetch(buildApiEndpoint('/game-events', true), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                eventType: 'trivia',
                                eventData: { triviaId }
                            })
                        });
                    } catch (error) {
                        console.error('Error syncing trivia:', triviaId, error);
                    }
                }
            }

            // Send all guesses
            if (localGame.guesses?.length) {
                for (const guess of localGame.guesses) {
                    try {
                        await fetch(buildApiEndpoint('/game-events', true), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                eventType: 'guess',
                                eventData: {
                                    guess: {
                                        firstname: guess.firstname,
                                        middlename: guess.middlename,
                                        lastname: guess.lastname
                                    }
                                }
                            })
                        });
                    } catch (error) {
                        console.error('Error syncing guess:', guess, error);
                    }
                }
            }

            console.log('Local game sync completed');

            // Clear the local storage after successful sync
            StorageHelper.removeItem(gameName);
            console.log('Local game data cleared from storage');

        } catch (error) {
            console.error('Error syncing local game to server:', error);
        }
    }
}

const useGameState = (loggedIn: boolean, token?: string): { game: Game | null, loading: boolean, recordQuestion: (id: number) => Promise<number>, recordTrivia: (id: number) => Promise<number>, recordGuess: (guess: Guess) => Promise<number>, updateGameCompletion: (completed: boolean) => void, forceRefresh: () => Promise<void>, updateGameState: (loggedIn: boolean, token?: string) => Promise<void> } => {
    const [game, setGame] = useState<Game | null>(null);
    const [loading, setLoading] = useState(true);
    const gameManager = GameManager.getInstance();

    useEffect(() => {
        const initializeGame = async () => {
            setLoading(true);
            try {
                const gameData = await gameManager.initialize(loggedIn, token);
                setGame(gameData);
            } catch (error) {
                console.error('Error initializing game:', error);
            } finally {
                setLoading(false);
            }
        };

        initializeGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedIn, token]);

    const recordQuestion = async (id: number): Promise<number> => {
        const newScore = await gameManager.recordQuestion(id);
        // Update local state efficiently without full reload
        if (game) {
            const updatedGame = {
                ...game,
                asks: [...game.asks, id],
                score: newScore
            };
            setGame(updatedGame);
        }
        return newScore;
    };

    const recordTrivia = async (id: number): Promise<number> => {
        const newScore = await gameManager.recordTrivia(id);
        // Update local state efficiently without full reload
        if (game) {
            const updatedGame = {
                ...game,
                triviaAsks: [...game.triviaAsks, id],
                score: newScore
            };
            setGame(updatedGame);
        }
        return newScore;
    };

    const recordGuess = async (guess: Guess): Promise<number> => {
        // Make server call and get the actual score
        const newScore = await gameManager.recordGuess(guess);

        // Update game state with server response
        // This will trigger useEffect in components to sync their local state
        if (game) {
            const updatedGame = {
                ...game,
                guesses: [...game.guesses, guess],
                score: newScore
            };
            setGame(updatedGame);
        }

        return newScore;
    };

    const forceRefresh = async (): Promise<void> => {
        try {
            const refreshedGame = await gameManager.forceReload();
            setGame(refreshedGame);
        } catch (error) {
            console.error('Error force refreshing game state:', error);
        }
    };

    const updateGameState = async (newLoggedIn: boolean, newToken?: string): Promise<void> => {
        try {
            setLoading(true);
            const updatedGame = await gameManager.updateGameState(newLoggedIn, newToken);
            setGame(updatedGame);
        } catch (error) {
            console.error('Error updating game state:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateGameCompletion = (completed: boolean): void => {
        gameManager.updateGameCompletion(completed);
        // Also update the local state immediately
        if (game) {
            setGame(prev => prev ? { ...prev, gameCompleted: completed } : prev);
        }
    };

    return { game, loading, recordQuestion, recordTrivia, recordGuess, updateGameCompletion, forceRefresh, updateGameState };
}

export { GameManager, useGameState };